import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of, tap, switchMap } from 'rxjs';
import { UserService } from '../../api/user/user.service';
import { AdministratorDTO } from 'src/app/shared/models/dtos/responses/administrator-dto/administrator-dto';
import { AdminStatus } from 'src/app/shared/models/constants/adminStatus';
import { Router } from '@angular/router';
import { RoutesKeys } from 'src/app/utils/routes';
import { LocalStorageKeys } from 'src/app/shared/models/constants/storage.keys';
import { Roles } from 'src/app/utils/roles';
import { Region } from 'src/app/shared/models/dtos/region/region';
import { first } from 'lodash';

@Injectable({
  providedIn: 'root',
})
/**
 * Service used to interact with current authenticated user
 */
export class AuthenticatedUserService {
  private _admin?: AdministratorDTO;
  private _currentRegion?: Region;
  admin$ = new BehaviorSubject<AdministratorDTO | undefined>(undefined);
  registerStatus$ = new BehaviorSubject<string | undefined>(undefined);
  currentRegion$ = new BehaviorSubject<Region | undefined>(undefined);

  /**
   * @constructor
   */
  constructor(
    private readonly userService: UserService,
    private router: Router
  ) {}

  /**
   * @param {string | undefined } value
   */
  set currentRegion(value: Region | undefined) {
    if (value) {
      this._currentRegion = value;
      this.currentRegion$.next(value);
      localStorage.setItem(
        LocalStorageKeys.CURRENT_REGION,
        JSON.stringify(value)
      );
    }
  }

  /**
   * Gets currentRegion
   */
  get currentRegion() {
    return this._currentRegion;
  }

  /**
   * @param {AdministratorDTO | undefined } value
   */
  set admin(value: AdministratorDTO | undefined) {
    this._admin = value;
    this.admin$.next(value);
  }

  /**
   * Gets admin
   */
  get admin() {
    return this._admin;
  }

  /**
   * Initializes admin if possible
   * @return {Observable}
   */
  initializeAdmin() {
    if (!this.admin) {
      return this.userService.retrieveAdminLoginInfo().pipe(
        switchMap((adminRegister) => {
          if (adminRegister.status === AdminStatus.INCOMPLETE) {
            return of({
              admin: { ...adminRegister, picture: '' },
              status: adminRegister.status,
            });
          }
          return this.userService
            .getAdminInfo()
            .pipe(map((admin) => ({ admin, status: adminRegister.status })));
        }),
        tap(({ admin, status }) => {
          if (admin.regions) {
            admin.regions.sort((a, b) => a.name.localeCompare(b.name));
          }
          if (
            localStorage.getItem(LocalStorageKeys.GLOBAL_JUST_LOGGED) ===
              null &&
            admin.role === Roles.GLOBAL_ADMIN
          ) {
            localStorage.setItem(LocalStorageKeys.GLOBAL_JUST_LOGGED, '');
            this.router.navigate([RoutesKeys.CONTROL_PANEL]);
          }
          this.registerStatus$.next(status as AdminStatus);
          this.admin = admin;
          this.initializeCurrentAdminRegion(admin);
        })
      );
    }
    return of({
      admin: this.admin$.getValue(),
      status: this.registerStatus$.getValue(),
    });
  }

  /**
   * Resets current admin
   * @return {Observable}
   */
  resetAdmin() {
    this.admin = undefined;
    return this.initializeAdmin();
  }

  /**
   * Updates sys admin's language and updates
   * internal admin reference
   * @param {number} regionId
   * @param {string} language
   * @param {string} name
   * @param {string} picture
   * @return {Observable<AdministratorDTO>}
   */
  updateOnboardingData(
    regionId: number,
    language: 'en' | 'pt' | 'es',
    name: string,
    picture?: string
  ): Observable<AdministratorDTO> {
    return this.userService
      .updateOnboardingData(regionId, language, name, picture)
      .pipe(
        tap((admin) => {
          this.admin = admin;
          this.initializeCurrentAdminRegion(admin);
          this.registerStatus$.next('');
        })
      );
  }

  /**
   * Checks if Admin has View Only role
   * @return {Boolean}
   */
  isVOAdmin(): boolean {
    return this.admin?.role === Roles.VIEW_ADMIN;
  }

  /**
   * Checks if Admin has Call Center Agent role
   * @return {Boolean}
   */
  isCallCenterAgent(): boolean {
    return this.admin?.role === Roles.CALL_CENTER_AGENT;
  }

  /**
   * Checks if Admin has Regional role
   * @return {Boolean}
   */
  isRegionalAdmin(): boolean {
    return this.admin?.role === Roles.REGIONAL_ADMIN;
  }

  /**
   * Checks if Admin has Global role
   * @return {Boolean}
   */
  isGlobalAdmin(): boolean {
    return this.admin?.role === Roles.GLOBAL_ADMIN;
  }

  /**
   * Initializes the current region of the admin
   * @param {AdministratorDTO} admin
   */
  private initializeCurrentAdminRegion(admin: AdministratorDTO) {
    const currentRegion = localStorage.getItem(LocalStorageKeys.CURRENT_REGION);
    if (currentRegion === 'undefined' || currentRegion === null) {
      this.currentRegion = first(admin.regions);
    } else {
      const locallyStoredRegion: Region = JSON.parse(currentRegion ?? '');
      const stillValidRegion = admin.regions.find(
        (region) => region.id === locallyStoredRegion.id
      );
      this.currentRegion = stillValidRegion ?? first(admin.regions);
    }
  }
}
